import { isBrowser } from 'browser-or-node'
import { useI18n } from 'vue-i18n'

export function useCustomFields() {
    const { $getI18n } = useNuxtApp()
    const translator = $getI18n

    const { t: $t } = useI18n()

    const stringCompare = (trans) => (a, b) => {
        let aString = a || ''
        let bString = b || ''
        if (typeof a === 'object') {
            aString = trans(aString)
        }
        if (typeof b === 'object') {
            bString = trans(bString)
        }
        return aString.localeCompare(bString)
    }

    const booleanCompare = (a, b) => {
        if (a === b) {
            return 0
        } else if (a === true || b === false) {
            return 1
        } else if (a === false || b === true) {
            return -1
        }
    }
    const dateCompare = (a, b) => {
        if (a && b) {
            const aT = new Date(a).getTime()
            const bT = new Date(b).getTime()
            return aT > bT ? 1 : aT < bT ? -1 : 0
        } else if (a || b) {
            return 1
        }
    }

    function renderWithTypeAndAffixes(project, key, value, def = '') {
        if (value === null || value === undefined) return '-'
        return renderWithAffixes(project, key, renderWithType(project, key, value), def)
    }

    function renderWithType(project, key, value) {
        if (value === null || value === undefined) return '-'
        const field = getField(project, key)
        if (!field || !field.type) return value

        if (!value) return '-'

        switch (field.type) {
            case 'currency':
                return formatCurrency(value, project.currency)
            case 'area':
                return `${value} ${project.sizeUnit}`
            case 'date':
                let date = value
                if (!date) return ''
                if (!(date instanceof Date)) {
                    date = new Date(value)
                }
                return date.toLocaleDateString()
            case 'boolean':
                return value ? ($t ? $t('yes') : 'yes') : $t ? $t('no') : 'no'
            case 'string':
            case 'url':
            case 'html':
                if (typeof value === 'string') return value
                if (value) return translator(value)
                return value
            case 'id':
            case 'number':
                return value
            case 'status':
                if (!project || !project.statuses) return ''
                const status = project.statuses.find((s) => s.id === value)
                if (status) return translator(status.label)
                return ''
            default:
                return value
        }
    }

    function renderWithAffixes(project, key, value, defaultValue = '') {
        if (value === null || value === undefined) return defaultValue

        const field = getField(project, key)
        if (!field) return value

        const prefix = field.prefix ? translator(field.prefix) : ''
        const suffix = field.suffix ? translator(field.suffix) : ''
        return `${prefix} ${value} ${suffix}`.trim()
    }

    function renderer(project, type, key, value) {
        switch (type) {
            case 'url':
                return translator(value).split('|')[0]
            case 'string':
            case 'html':
            case 'status':
            case 'id':
            case 'date':
            case 'number':
            case 'currency':
            case 'area':
            case 'boolean':
                return renderWithType(project, key, value)
        }
    }

    function getSortFunction(type) {
        switch (type) {
            case 'string':
            case 'html':
            case 'url':
                return stringCompare(translator)
            case 'boolean':
                return booleanCompare
            case 'date':
                return dateCompare
            default:
                return undefined
        }
    }

    function formatCurrency(amount, currency) {
        let options = {}

        // if (amount > 100000) {
        //     // options = { notation: 'compact' }
        // }

        return Intl.NumberFormat(isBrowser ? navigator.language : 'en', {
            ...options,
            style: 'currency',
            minimumFractionDigits: 0,
            currency
        }).format(amount)
    }

    function getStatus(project, unit) {
        return project.statuses.find(
            (s) => s.id === ('rowData' in unit ? unit.rowData.status : unit.status)
        )
    }
    function getField(project, key) {
        return [...(project.fields || []), ...project.internalFields].find((f) => f.key === key)
    }
    function getIllegalFieldKeys(project) {
        return [...(project.fields || []), ...project.internalFields].map((f) => f.key)
    }
    function getFieldStatusOption(project, unit, key) {
        const field = getField(project, key)
        const status = getStatus(project, unit)

        let fieldStatusOption = null

        if (field) {
            if (field.statusOptions) {
                fieldStatusOption = field.statusOptions.find(
                    (so) => so.projectStatusId === status.id
                )
            }
        }

        return fieldStatusOption
            ? fieldStatusOption
            : {
                  projectFieldId: field.id,
                  projectStatusId: status.id,
                  hide: false,
                  customRenderer: null,
                  hideRenderer: null
              }
    }
    function getStatusTextColor(status, key = 'activeColor') {
        return status[key].substring(0, 7)
    }

    return {
        renderer,
        getStatusTextColor,
        getField,
        getSortFunction,
        getFieldStatusOption,
        renderWithTypeAndAffixes,
        renderWithType,
        renderWithAffixes,
        formatCurrency,
        getStatus,
        getIllegalFieldKeys
    }
}
